import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from "randmarcomps";
import { useState, FC } from "react";
import InlineSpinner from "./InlineSpinner";

interface IProps {
  name: string;
  onConfirm: (invoiceNumber: string, invoiceDate: string, invoiceTotal: number) => void;
}

const InvoicePanel: FC<IProps> = ({ name, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [invoiceTotal, setInvoiceTotal] = useState<number | "">("");

  const handleCancel = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (invoiceTotal === "") return;
    setLoading(true);
    // This is a fire and forget call, we are keeping the original behavior.
    onConfirm(invoiceNumber, invoiceDate, invoiceTotal);
    setLoading(false);
    setOpen(false);
  };

  const handleInvoiceTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "") {
      setInvoiceTotal("");
    } else {
      const num = parseFloat(value);
      if (!isNaN(num) && num >= 0) {
        setInvoiceTotal(num);
      }
    }
  };

  return (
    <>
      <Button
        title={name}
        onClick={() => setOpen(true)}
      >
        {name}
      </Button>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && handleCancel()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invoice Document</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invoiceNumber">Invoice Number</Label>
              <Input
                id="invoiceNumber"
                required
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceDate">Invoice Date</Label>
              <Input
                id="invoiceDate"
                required
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceTotal">Amount</Label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <Input
                  id="invoiceTotal"
                  required
                  type="number"
                  min={0}
                  step="0.01"
                  value={invoiceTotal}
                  onChange={handleInvoiceTotalChange}
                  className="pl-7"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={
                invoiceNumber === "" ||
                invoiceDate === "" ||
                invoiceTotal === "" ||
                loading
              }
            >
              {loading ? <InlineSpinner className="mr-2" decorative /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvoicePanel;
